import { BehaviorSubject, from, of } from 'rxjs';
import { catchError, filter, finalize, first, map } from 'rxjs/operators';
import * as Configuration from '../../app.constants.js';

export const JWT_LOCAL_STORAGE_KEY = 'jwt';
export const JWT_SHORT_LOCAL_STORAGE_KEY = 'jwt-short';
export const JWT_SHORT_TIMESTAMP_LOCAL_STORAGE_KEY = 'jwt-short-timestamp';
export const JWT_SHORT_LIFETIME = 5 * 60 * 1000;
export const ADMIN_USER_LOCAL_STORAGE_KEY = '-warn-admin-user-back-office';


export const LOGIN_OPTION_TYPE_ALCMEON = 0; // must match CompanyLoginOptionType in ModelConstants.py
export const LOGIN_OPTION_TYPE_SSO = 1;

const COMPANIES_WITH_LEGACY_SAML_ENDPOINTS = [88]; // 88 is Carrefour company id. The behaviour is very specific and will not change.

export class LoginService {
  constructor($http, $state, TopToolbarFactory, LocalStorageFactory, UserFactory, AngularMigrationHelperFactory, AlertingFactory) {
    this._$http = $http;
    this._$state = $state;
    this.topToolbarFactory = TopToolbarFactory;
    this.localStorageFactory = LocalStorageFactory;
    this.UserFactory = UserFactory;
    this._shouldReloadLatestClient = false;
    this.AngularMigrationHelperFactory = AngularMigrationHelperFactory;
    this.AlertingFactory = AlertingFactory;
    this.companyId = $state.params.companyId;

    if (!this.companyId && window.location && window.location.hash) {
      let path = window.location.hash;
      let match = new RegExp("#/([0-9]+)").exec(path)
      if (match !== null) {
        this.companyId = parseInt(match[1])
      }
    }

    if (this.companyId) {
      let jwt = this.localStorageFactory.getItem(this.companyId + '-' + JWT_LOCAL_STORAGE_KEY);
      this._setJwt(this.companyId, jwt);

      if (this.localStorageFactory.getItem(this.companyId + ADMIN_USER_LOCAL_STORAGE_KEY)) {
        this.topToolbarFactory.setWarnAdminUser(true);
      }
    }

    this._confirmLogoutCallback = null; // () => Promise<boolean>

    this._jwtShort = this.localStorageFactory.getItem(this.companyId + '-' + JWT_SHORT_LOCAL_STORAGE_KEY);
    this._jwtShort$ = new BehaviorSubject('');
    this._jwtShortTimestamp = this.localStorageFactory.getItem(this.companyId + '-' + JWT_SHORT_TIMESTAMP_LOCAL_STORAGE_KEY);
    this._requestLock = false;
  }

  getJwt() {
    return this._jwt;
  }

  /**
   * Get a jwt short lived, with cache and concurrency management
   */
  getJwtShort() {
    // if jwt exist and < lifetime
    if (this._jwtShort && this._jwtShortTimestamp >= Date.now() - JWT_SHORT_LIFETIME) {
      return of(this._jwtShort);
    }
    // wait request in progress
    else if (this._requestLock) {
      return this._jwtShort$.pipe(filter(jwt => !!jwt && !this._requestLock));
    }
    // first call or renew
    else {
      const url = Configuration.constants.baseUrl + 'api/1.0/companies/' + this.companyId + '/create-temporary-jwt';
      this._requestLock = true;
      return from(this._$http.post(url, null)).pipe(
        map(response => {
          this._jwtShort = response.data.jwt;
          this._jwtShortTimestamp = Date.now();
          this._requestLock = false;
          this._jwtShort$.next(response.data.jwt);
          this.localStorageFactory.setItem(this.companyId + '-' + JWT_SHORT_LOCAL_STORAGE_KEY, this._jwtShort);
          this.localStorageFactory.setItem(this.companyId + '-' + JWT_SHORT_TIMESTAMP_LOCAL_STORAGE_KEY, this._jwtShortTimestamp);
          return response.data.jwt;
        }),
        catchError(() => {
          this._jwtShort$.next('');
          return of('');
        }),
        finalize(() => (this._requestLock = false))
      );
    }
  }

  isLoggedUserInternal() {
    return this.UserFactory.isLoggedUserInternal();
  }

  _setJwt(companyId, jwt) {
    this._jwt = jwt;
    this.AngularMigrationHelperFactory.ajsJWT$.next(jwt);
    if (companyId) {
      this.localStorageFactory.setItem(companyId + '-' + JWT_LOCAL_STORAGE_KEY, this._jwt);
    }
  }

  addJwtToUrl(url) {
    return (/\?/.exec(url)) ? (url + '&jwt=' + this._jwt) : (url + '?jwt=' + this._jwt);
  }

  addJwtShortToUrl(url) {
    return new Promise((resolve, reject) => {
      this.getJwtShort()
        .pipe(first())
        .subscribe((jwtShort) => {
          resolve(/\?/.exec(url) ? url + '&jwt=' + jwtShort : url + '?jwt=' + jwtShort);
        });
    });
  }

  redirectWithJwtShort(url) {
    this.addJwtShortToUrl(url).then((urlWithJwt) => {
      window.location.href = urlWithJwt;
    });
  }

  doBackofficeLogin(companyId, jwtShort, destUrl, warnAdminUser) {
    this.topToolbarFactory.setWarnAdminUser(warnAdminUser);
    if (warnAdminUser) {
      this.localStorageFactory.setItem(companyId + ADMIN_USER_LOCAL_STORAGE_KEY, true);
    } else {
      this.localStorageFactory.removeItem(companyId + ADMIN_USER_LOCAL_STORAGE_KEY);
    }

    // use jwt short globally in order to get jwt long, in the following post (via httpInterceptorsConfig)
    this._setJwt(companyId, jwtShort);

    const url = Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/create-backoffice-final-jwt';
    this._$http.post(url, null).then(reponse => {
      this.doSamlLogin(companyId, reponse.data.jwt, destUrl);
    });
  }

  doSamlLogin(companyId, jwt, destUrl) {
    this._promoteLoginOptionFromTentativeToSuccessful(companyId);
    // when login with SSO (backoffice or client SSO), companyId is not in $state.params neither in URL (see in constructor)
    // and page is not refreshed, it's just a state change
    this.companyId = companyId;
    this._setJwt(companyId, jwt);
    this.UserFactory.clearLoggedUser();
    window.location.href = destUrl;
  }


  logout(company) {
    if (this._confirmLogoutCallback) {
      this._confirmLogoutCallback()
        .then(wasConfirmed => {
          if (wasConfirmed) {
            this._doLogout(company);
          }
        });
    } else {
      this._doLogout(company);
    }
  }


  _doLogout(company) {
    if (this._wasLastLoginViaSSO(company.id)) {
      this._doSamlLogout(company.id);
    } else {
      this._doClassicLogout(company.id, company.url_name);
    }
  }

  _doClassicLogout(companyId, companyUrlName) {
    this._$http.post(Configuration.constants.baseUrl + companyId + '/logout')
      .then(response => {
        this._setJwt(companyId, null);
        if (Configuration.constants.isBackofficeActivated && this.isLoggedUserInternal()) {
          this._$state.go('no-auth.company.logout-successful', { companyName: companyUrlName });
        } else {
          this._$state.go('no-auth.company.login', { companyName: companyUrlName });
        }
      });
  }

  _doSamlLogout(companyId) {
    const samlConfigId = this._getPastSuccessfulLoginOption(companyId).saml_configuration_id;
    const suffix = COMPANIES_WITH_LEGACY_SAML_ENDPOINTS.includes(companyId) ? `/saml/initiate-slo` : `/saml/${samlConfigId}/initiate-slo`;
    this._$http.post(Configuration.constants.baseUrl + companyId + suffix)
      .then(response => window.location.href = response.data.redirect_to,
        this.AlertingFactory.httpErrorHandler());
  }

  setConfirmLogoutCallback(callback) {
    this._confirmLogoutCallback = callback; // () => Promise<boolean>
  }


  doLogin(companyId, email, password, destState, destUrl) {
    // when login with login/mdp, companyId is not in $state.params neither in URL (see in constructor)
    // and page is not refreshed, it's just a state change
    this.companyId = companyId;
    let self = this;

    this.localStorageFactory.removeItem(companyId + ADMIN_USER_LOCAL_STORAGE_KEY);
    this.topToolbarFactory.setWarnAdminUser(false);

    function gotoDefault() {
      self._$state.go('auth.alcmeon.after-login', {
        companyId: companyId
      });
    }
    return this._$http.post(Configuration.constants.baseUrl + companyId + '/login', { email: email, password: password })
      .then((response) => {
        this._promoteLoginOptionFromTentativeToSuccessful(companyId);
        this._setJwt(companyId, response.data.jwt);
        this.computeShouldReloadLatestClient(response.data.latest_hash);
        self.UserFactory.clearLoggedUser();
        if (destState !== null) {
          self._$state.go(destState.name, destState.params);
        } else if (destUrl !== null) {
          window.location.href = destUrl;
        } else {
          gotoDefault();
        }
      });
  }

  shouldReloadLatestClient() {
    return this._shouldReloadLatestClient;
  }

  clearShouldReloadLatestClient() {
    this._shouldReloadLatestClient = false;
  }

  computeShouldReloadLatestClient(latestBuildTimestamp) {
    const isLocalhost = window.location.hostname === 'localhost';

    // On local dev server, no build timestamp is generated, but there is autoreload anyway
    this._shouldReloadLatestClient = isLocalhost ? false : latestBuildTimestamp !== ALC_WEBPACK_BUILD_TIMESTAMP;
  }

  initiateSSO(companyId, samlConfigId, finalUrl) {
    const data = {
      redirect_to: finalUrl,
    }
    const suffix = COMPANIES_WITH_LEGACY_SAML_ENDPOINTS.includes(companyId) ? `/saml/initiate-sso` : `/saml/${samlConfigId}/initiate-sso`;
    this._$http.post(Configuration.constants.baseUrl + companyId + suffix, data)
      .then(response => window.location.href = response.data.redirect_to,
        this.AlertingFactory.httpErrorHandler());
  }

  /**
   * Builds the state parameters that needs to be passed along the 
   * pre-login -> login-option-selection -> pre-login -> login-in-alcmeon process,
   * so that the user can be redirected to its intended destination after the login,
   * or so that the appropriate error message can be displayed on the login page.
   */
  stateParamsToPropagate() {
    return {
      destUrl: this._$state.params.destUrl,
      destState: this._$state.params.destState,
      errorMessageKey: this._$state.params.errorMessageKey,
    }
  }

  _wasLastLoginViaSSO(companyId) {
    const lastSucessfulLoginOption = this._getPastSuccessfulLoginOption(companyId);
    return lastSucessfulLoginOption && lastSucessfulLoginOption.type === LOGIN_OPTION_TYPE_SSO;
  }

  saveTentativeLoginOption(companyId, loginOption) {
    this.localStorageFactory.setItem(this._tentativeLoginOptionLSKey(companyId), loginOption);
  }

  saveIsRememberMyChoice(companyId, isRememberMyChoice) {
    this.localStorageFactory.setItem(this._rememberMyChoiceLSKey(companyId), isRememberMyChoice);
  }

  rememberedLoginOption(companyId) {
    if (this.localStorageFactory.getItem(this._rememberMyChoiceLSKey(companyId))) {
      return this._getPastSuccessfulLoginOption(companyId);
    } else {
      return null;
    }
  }

  clearRememberMyChoice(companyId) {
    this.localStorageFactory.removeItem(this._rememberMyChoiceLSKey(companyId));
  }

  // called upon a successful login, so that if the user checked the "remember my choice" checkbox,
  // we can skip the login-options page in the future
  _promoteLoginOptionFromTentativeToSuccessful(companyId) {
    const promoted = this.localStorageFactory.getItem(this._tentativeLoginOptionLSKey(companyId));
    if (promoted) {
      this.localStorageFactory.setItem(this._successfulLoginOptionLSKey(companyId), promoted);
      this.localStorageFactory.removeItem(this._tentativeLoginOptionLSKey(companyId));
    }
  }

  _getPastSuccessfulLoginOption(companyId) {
    return this.localStorageFactory.getItem(this._successfulLoginOptionLSKey(companyId));
  }


  // As the SSO proccess happens on another webapp, we need to remember the login option that was selected by the user
  // (and only store that option as the sucessful option if the login is sucessful)
  _tentativeLoginOptionLSKey(companyId) {
    return `${companyId}-tentative-login-option`;
  }

  _successfulLoginOptionLSKey(companyId) {
    return `${companyId}-successful-login-option`;
  }

  _rememberMyChoiceLSKey(companyId) {
    return `${companyId}-is-remember-login-option`;
  }

  static LoginFactory($http, $state, TopToolbarFactory, LocalStorageFactory, UserFactory, AngularMigrationHelperFactory, AlertingFactory) {
    return new LoginService($http, $state, TopToolbarFactory, LocalStorageFactory, UserFactory, AngularMigrationHelperFactory, AlertingFactory);
  }
}
LoginService.LoginFactory.$inject = ['$http', '$state', 'TopToolbarFactory', 'LocalStorageFactory', 'UserFactory', 'AngularMigrationHelperFactory', 'AlertingFactory'];

